<template>
  <div class="h-full overflow-auto p-6 bg-gray-50">

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Message Delivery Report</h2>
        <p v-if="report && report.generated_at" class="text-sm text-gray-500 mt-0.5">
          Generated {{ timeAgo(report.generated_at) }}
        </p>
        <p v-else class="text-sm text-gray-500 mt-0.5">No report generated yet</p>
      </div>
      <button
        class="px-4 py-2 text-sm font-medium rounded-md border transition-colors"
        :class="generating
          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
        :disabled="generating"
        @click="refresh"
      >
        <font-awesome-icon v-if="generating" :icon="['fas', 'circle-notch']" spin class="mr-1.5" />
        <font-awesome-icon v-else :icon="['fas', 'rotate']" class="mr-1.5" />
        {{ generating ? 'Generating…' : 'Generate Report' }}
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-20 text-gray-400 text-sm">
      <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-2" />
      Loading…
    </div>

    <!-- Empty state -->
    <div v-else-if="!report || !report.generated_at" class="flex items-center justify-center py-20">
      <div class="text-center max-w-sm">
        <font-awesome-icon :icon="['fas', 'chart-bar']" class="text-gray-300 text-4xl mb-4" />
        <p class="text-sm text-gray-500">
          Click <strong>Generate Report</strong> to pull delivery status from Updox.
          This may take up to a minute.
        </p>
      </div>
    </div>

    <!-- Report content -->
    <div v-else>

      <!-- Stats row -->
      <div class="grid grid-cols-4 gap-4 mb-6">
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">Users</div>
          <div class="text-2xl font-semibold mt-1">{{ report.total_users }}</div>
          <div class="text-xs text-gray-400 mt-0.5">{{ report.active_users }} active</div>
        </div>
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Users</div>
          <div class="text-2xl font-semibold mt-1">{{ report.users_with_activity }}</div>
          <div class="text-xs text-gray-400 mt-0.5">with messages</div>
        </div>
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">External Sends</div>
          <div class="text-2xl font-semibold mt-1">{{ report.total_external_sends }}</div>
          <div class="text-xs text-gray-400 mt-0.5">to outside orgs</div>
        </div>
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <div class="text-xs font-medium uppercase tracking-wide"
            :class="failureRate > 0 ? 'text-red-600' : 'text-gray-500'">
            Failure Rate
          </div>
          <div class="text-2xl font-semibold mt-1" :class="failureRate > 0 ? 'text-red-600' : ''">
            {{ failureRate }}%
          </div>
          <div class="text-xs text-gray-400 mt-0.5">{{ report.failed_external_sends }} of {{ report.total_external_sends }} failed</div>
        </div>
      </div>

      <!-- Report notes -->
      <div class="bg-white border border-gray-200 rounded-lg mb-6">
        <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-900">Notes</h3>
          <button v-if="!isEditing('report', 'report')" @click="startEdit('report', 'report')"
            class="text-xs text-blue-600 hover:text-blue-800">
            {{ getNoteText('report', 'report') ? 'Edit' : 'Add note' }}
          </button>
        </div>
        <div class="px-4 py-3">
          <div v-if="isEditing('report', 'report')">
            <textarea v-model="editingText" rows="3"
              class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add notes about this report…"></textarea>
            <div class="flex justify-end gap-2 mt-2">
              <button @click="cancelEdit" class="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
              <button @click="saveNote('report', 'report')" class="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
            </div>
          </div>
          <div v-else-if="getNoteText('report', 'report')">
            <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ getNoteText('report', 'report') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ getNoteMetadata('report', 'report') }}</p>
          </div>
          <p v-else class="text-sm text-gray-400 italic">No notes yet</p>
        </div>
      </div>

      <!-- EHR Summary table -->
      <div class="bg-white border border-gray-200 rounded-lg mb-6">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">Delivery Status by EHR / HISP</h3>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th class="px-4 py-2">Domain</th>
              <th class="px-4 py-2">Sends</th>
              <th class="px-4 py-2">Delivered</th>
              <th class="px-4 py-2">Failed</th>
              <th class="px-4 py-2">Status</th>
              <th class="px-4 py-2">Failure Reason</th>
              <th class="px-4 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ehr in report.ehr_summary" :key="ehr.domain"
              class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-4 py-2.5 font-mono text-xs">
                <span v-if="ehr.is_internal" class="text-gray-400">{{ ehr.domain }} (internal)</span>
                <span v-else>{{ ehr.domain }}</span>
              </td>
              <td class="px-4 py-2.5">{{ ehr.total_sends }}</td>
              <td class="px-4 py-2.5 text-green-700">{{ ehr.delivered }}</td>
              <td class="px-4 py-2.5" :class="ehr.failed > 0 ? 'text-red-600 font-medium' : ''">{{ ehr.failed }}</td>
              <td class="px-4 py-2.5">
                <span class="inline-block px-2 py-0.5 rounded text-xs font-medium"
                  :class="statusClass(ehr.status)">
                  {{ ehr.status }}
                </span>
              </td>
              <td class="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate">
                {{ ehr.failure_reasons.join('; ') || '—' }}
              </td>
              <td class="px-4 py-2.5 text-xs min-w-[200px]">
                <div v-if="isEditing('ehr_domain', ehr.domain)">
                  <textarea v-model="editingText" rows="2"
                    class="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add note…"
                    @keydown.escape="cancelEdit"></textarea>
                  <div class="flex justify-end gap-1 mt-1">
                    <button @click="cancelEdit" class="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    <button @click="saveNote('ehr_domain', ehr.domain)" class="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
                  </div>
                </div>
                <div v-else class="group cursor-pointer" @click="startEdit('ehr_domain', ehr.domain)">
                  <span v-if="getNoteText('ehr_domain', ehr.domain)" class="text-gray-700">{{ getNoteText('ehr_domain', ehr.domain) }}</span>
                  <span v-else class="text-gray-300 opacity-0 group-hover:opacity-100">+ add note</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- External sends table -->
      <div class="bg-white border border-gray-200 rounded-lg mb-6">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">All External Sends</h3>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th class="px-4 py-2">Date</th>
              <th class="px-4 py-2">From</th>
              <th class="px-4 py-2">Recipient</th>
              <th class="px-4 py-2">Subject</th>
              <th class="px-4 py-2">Body</th>
              <th class="px-4 py-2">Attachments</th>
              <th class="px-4 py-2">Delivery</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="send in sortedExternalSends" :key="send.message_id"
              class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{{ formatDate(send.sent_utc) }}</td>
              <td class="px-4 py-2.5 text-xs">{{ send.sender_name }}</td>
              <td class="px-4 py-2.5 font-mono text-xs max-w-[200px] truncate" :title="send.recipient_direct_address">
                {{ send.recipient_direct_address }}
              </td>
              <td class="px-4 py-2.5 text-xs max-w-[160px] truncate">{{ send.subject }}</td>
              <td class="px-4 py-2.5 text-xs text-gray-500">
                {{ send.body_is_html ? 'HTML' : 'Plain text' }}
              </td>
              <td class="px-4 py-2.5 text-xs">
                <span v-if="send.attachments.length === 0" class="text-gray-400">—</span>
                <span v-else v-for="(att, i) in send.attachments" :key="i"
                  class="inline-block bg-gray-100 rounded px-1.5 py-0.5 mr-1 mb-0.5 text-gray-600">
                  {{ att.file_name }} <span class="text-gray-400">({{ formatSize(att.size_bytes) }})</span>
                </span>
              </td>
              <td class="px-4 py-2.5">
                <span class="inline-block px-2 py-0.5 rounded text-xs font-medium"
                  :class="mdnClass(send.mdn_category)">
                  {{ send.mdn_category }}
                </span>
              </td>
            </tr>
            <tr v-if="sortedExternalSends.length === 0">
              <td colspan="7" class="px-4 py-6 text-center text-gray-400 text-sm">No external messages sent</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Failure notifications -->
      <div v-if="report.fail_notifications.length > 0" class="bg-white border border-gray-200 rounded-lg mb-6">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">Failure Notifications</h3>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th class="px-4 py-2">Time</th>
              <th class="px-4 py-2">Sender</th>
              <th class="px-4 py-2">Failed Recipient</th>
              <th class="px-4 py-2">Error</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(fn, i) in report.fail_notifications" :key="i"
              class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{{ formatDate(fn.received_utc) }}</td>
              <td class="px-4 py-2.5 font-mono text-xs">{{ fn.user_direct_address }}</td>
              <td class="px-4 py-2.5 font-mono text-xs">{{ fn.failed_recipient }}</td>
              <td class="px-4 py-2.5 text-xs text-red-600 italic">{{ fn.failure_reason || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- User activity -->
      <div class="bg-white border border-gray-200 rounded-lg">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">User Activity</h3>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
              <th class="px-4 py-2">User</th>
              <th class="px-4 py-2">Direct Address</th>
              <th class="px-4 py-2">Verified</th>
              <th class="px-4 py-2">Inbox</th>
              <th class="px-4 py-2">Sent</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.direct_address"
              class="border-t border-gray-100 hover:bg-gray-50"
              :class="user.inbox_count === 0 && user.sent_count === 0 ? 'text-gray-400' : ''">
              <td class="px-4 py-2.5">{{ user.name }}</td>
              <td class="px-4 py-2.5 font-mono text-xs">{{ user.direct_address }}</td>
              <td class="px-4 py-2.5">
                <span v-if="user.verified" class="text-green-600 text-xs font-medium">Yes</span>
                <span v-else class="text-gray-400 text-xs">No</span>
              </td>
              <td class="px-4 py-2.5">{{ user.inbox_count }}</td>
              <td class="px-4 py-2.5">{{ user.sent_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import { getDeliveryReport, refreshDeliveryReport, getDeliveryReportNotes, saveDeliveryReportNote } from '@/services/api/providerMessaging'

export default {
  name: 'MessageDeliveryReport',
  data() {
    return {
      report: null,
      loading: false,
      generating: false,
      pollTimer: null,
      notes: {},
      editingNote: null,
      editingText: '',
    }
  },
  computed: {
    failureRate() {
      if (!this.report || this.report.total_external_sends === 0) return 0
      return Math.round((this.report.failed_external_sends / this.report.total_external_sends) * 100)
    },
    sortedUsers() {
      if (!this.report?.users) return []
      return [...this.report.users].sort((a, b) => {
        const aActive = a.inbox_count + a.sent_count
        const bActive = b.inbox_count + b.sent_count
        if (bActive !== aActive) return bActive - aActive
        return (a.name || '').localeCompare(b.name || '')
      })
    },
    sortedExternalSends() {
      if (!this.report?.external_sends) return []
      return [...this.report.external_sends].sort((a, b) => {
        const aSentAt = Date.parse(a.sent_utc) || 0
        const bSentAt = Date.parse(b.sent_utc) || 0
        return bSentAt - aSentAt
      })
    },
  },
  async mounted() {
    await this.loadReport()
    await this.loadNotes()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async loadReport() {
      this.loading = true
      try {
        const data = await getDeliveryReport()
        if (data.generated_at) {
          this.report = data
        }
        if (data.status === 'generating') {
          this.generating = true
          this.startPolling()
        }
      } finally {
        this.loading = false
      }
    },

    async refresh() {
      this.generating = true
      await refreshDeliveryReport()
      this.startPolling()
    },

    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        const data = await getDeliveryReport()
        if (data.status !== 'generating') {
          this.stopPolling()
          this.generating = false
          if (data.generated_at) {
            this.report = data
          }
          this.loadNotes()
        }
      }, 5000)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    noteKey(noteType, key) {
      return `${noteType}:${key}`
    },

    getNoteText(noteType, key) {
      const note = this.notes[this.noteKey(noteType, key)]
      return note ? note.text : ''
    },

    getNoteMetadata(noteType, key) {
      const note = this.notes[this.noteKey(noteType, key)]
      if (!note) return ''
      const who = note.updated_by_name || note.created_by_name || ''
      const when = this.timeAgo(note.updated_at)
      return who ? `${who} · ${when}` : when
    },

    isEditing(noteType, key) {
      return this.editingNote === this.noteKey(noteType, key)
    },

    startEdit(noteType, key) {
      this.editingNote = this.noteKey(noteType, key)
      this.editingText = this.getNoteText(noteType, key)
    },

    cancelEdit() {
      this.editingNote = null
      this.editingText = ''
    },

    async saveNote(noteType, key) {
      const text = this.editingText.trim()
      const saved = await saveDeliveryReportNote(key, noteType, text)
      this.notes[this.noteKey(noteType, key)] = saved
      this.editingNote = null
      this.editingText = ''
    },

    async loadNotes() {
      const notesArray = await getDeliveryReportNotes()
      const map = {}
      for (const n of notesArray) {
        map[this.noteKey(n.note_type, n.note_key)] = n
      }
      this.notes = map
    },

    statusClass(status) {
      if (status === 'working') return 'bg-green-100 text-green-800'
      if (status === 'failed') return 'bg-red-100 text-red-800'
      if (status === 'partial') return 'bg-yellow-100 text-yellow-800'
      return 'bg-gray-100 text-gray-600'
    },

    mdnClass(category) {
      if (category === 'delivered') return 'bg-green-100 text-green-800'
      if (category === 'failed') return 'bg-red-100 text-red-800'
      if (category === 'pending') return 'bg-yellow-100 text-yellow-800'
      return 'bg-gray-100 text-gray-600'
    },

    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    },

    formatSize(bytes) {
      if (!bytes) return '0B'
      if (bytes < 1024) return `${bytes}B`
      if (bytes < 1048576) return `${Math.round(bytes / 1024)}KB`
      return `${(bytes / 1048576).toFixed(1)}MB`
    },

    timeAgo(iso) {
      if (!iso) return ''
      const diff = Date.now() - new Date(iso).getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins}m ago`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `${hours}h ago`
      return `${Math.floor(hours / 24)}d ago`
    },
  },
}
</script>
